import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { generateSalesReport } from "../utils/pdfGenerator";
import BookingForm from "./BookingForm";

const getApptScheduleDetails = (apt) => {
  if (!apt) return { dateStr: "N/A", timeStr: "Not specified" };
  const firstPet = apt.pets?.[0];
  const dateObj = firstPet?.grooming?.date || firstPet?.hotel?.checkInDate || apt.appointmentDate || apt.createdAt;
  const time = firstPet?.grooming?.time || firstPet?.hotel?.checkInTime || apt.appointmentTime || "";
  const dateStr = dateObj ? new Date(dateObj).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "N/A";
  return { dateStr, timeStr: time || "Not specified" };
};

const AdminAppointments = ({ user, petFilter, onClearFilter, setActiveTab }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [isWalkInModalOpen, setIsWalkInModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [currentDate, setCurrentDate] = useState(new Date());

  // PDF Report Modal state
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfPreset, setPdfPreset] = useState("all"); // "all", "today", "yesterday", "week", "month", "last_month", "custom"
  const [pdfStartDate, setPdfStartDate] = useState("");
  const [pdfEndDate, setPdfEndDate] = useState("");
  const [pdfStatusFilter, setPdfStatusFilter] = useState("all");

  const getPdfFilteredData = () => {
    const now = new Date();
    let start = null;
    let end = null;
    let label = "All Time";

    if (pdfPreset === "today") {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      label = `Today (${start.toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })})`;
    } else if (pdfPreset === "yesterday") {
      const yest = new Date(now);
      yest.setDate(yest.getDate() - 1);
      start = new Date(yest.getFullYear(), yest.getMonth(), yest.getDate(), 0, 0, 0);
      end = new Date(yest.getFullYear(), yest.getMonth(), yest.getDate(), 23, 59, 59);
      label = `Yesterday (${start.toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })})`;
    } else if (pdfPreset === "week") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      start = new Date(weekAgo.getFullYear(), weekAgo.getMonth(), weekAgo.getDate(), 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      label = `Last 7 Days (${start.toLocaleDateString("en-US", { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })})`;
    } else if (pdfPreset === "month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      label = `This Month (${now.toLocaleString("default", { month: "long", year: "numeric" })})`;
    } else if (pdfPreset === "last_month") {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      label = `Last Month (${new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleString("default", { month: "long", year: "numeric" })})`;
    } else if (pdfPreset === "custom") {
      if (pdfStartDate && pdfEndDate) {
        const sParts = pdfStartDate.split("-");
        const eParts = pdfEndDate.split("-");
        start = new Date(Number(sParts[0]), Number(sParts[1]) - 1, Number(sParts[2]), 0, 0, 0);
        end = new Date(Number(eParts[0]), Number(eParts[1]) - 1, Number(eParts[2]), 23, 59, 59);
        label = `Custom (${start.toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })} to ${end.toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })})`;
      } else {
        label = "Custom Range (Select Dates)";
      }
    }

    const filteredApts = appointments.filter(a => {
      if (pdfStatusFilter !== "all" && a.status !== pdfStatusFilter) return false;
      if (start && end) {
        const dateVal = a.pets?.[0]?.appointmentDate || a.createdAt;
        if (!dateVal) return false;
        const d = new Date(dateVal);
        if (d < start || d > end) return false;
      }
      return true;
    });

    const totalRev = filteredApts
      .filter(a => a.status === "completed")
      .reduce((sum, a) => sum + (a.totalPrice || 0), 0);

    return { filteredApts, label, totalRev };
  };

  const handleGeneratePdf = () => {
    const { filteredApts, label } = getPdfFilteredData();
    generateSalesReport(filteredApts, { periodLabel: label });
    setIsPdfModalOpen(false);
  };

  const [appointmentDetailsModal, setAppointmentDetailsModal] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);

  const [actionModal, setActionModal] = useState({ isOpen: false, apt: null, type: null });
  const [actionForm, setActionForm] = useState({ reason: "", customReason: "", date: "", time: "", paymentMethod: "Cash", referenceNumber: "", exceedingHours: 0 });
  const [actionError, setActionError] = useState(null);

  const fetchAppointments = useCallback(async () => {
    try {
      const res = await axios.get("/api/appointments", {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.data.success) setAppointments(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => { 
    fetchAppointments(); 
  }, [fetchAppointments]);

  useEffect(() => {
    if (actionModal.isOpen) {
      const aptPaymentMethod = actionModal.apt?.paymentMethod || "Cash";
      const defaultExceeding = actionModal.apt?.pets?.find(p => p.hotel)?.hotel?.exceedingHours || 0;
      setActionForm({ 
        reason: "", 
        customReason: "", 
        date: "", 
        time: "", 
        paymentMethod: aptPaymentMethod, 
        referenceNumber: "",
        exceedingHours: defaultExceeding
      });
      setActionError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionModal.isOpen, actionModal.type]);

  const updateAppointmentData = async (id, payload) => {
    try {
      await axios.put(`/api/appointments/${id}`, payload, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      fetchAppointments();
      setActionModal({ isOpen: false, apt: null, type: null });
    } catch (err) {
      alert("Failed to update appointment");
    }
  };

  const handleActionSubmit = () => {
    const { apt, type } = actionModal;
    const finalReason = actionForm.reason === "Others" ? actionForm.customReason : actionForm.reason;

    if (type === "cancel" || type === "reject" || type === "reschedule") {
      if (!actionForm.reason) {
        setActionError("Please select a reason.");
        return;
      }
      if (actionForm.reason === "Others" && !actionForm.customReason.trim()) {
        setActionError("Please specify your reason in the text field.");
        return;
      }
    }
    setActionError(null);

    if (type === "reject") {
      updateAppointmentData(apt._id, { status: "rejected", rejectReason: finalReason, cancelReason: finalReason });
    } else if (type === "cancel") {
      updateAppointmentData(apt._id, { status: "cancelled", cancelReason: finalReason });
    } else if (type === "reschedule") {
      updateAppointmentData(apt._id, { 
        status: "confirmed", 
        rescheduleReason: finalReason,
      });
    } else if (type === "approve_cancel") {
      updateAppointmentData(apt._id, { status: "cancelled" });
    } else if (type === "approve_reschedule") {
      updateAppointmentData(apt._id, { 
        status: "confirmed", 
        rescheduleRequestedByCustomer: false
      });
    } else if (type === "reject_request") {
      updateAppointmentData(apt._id, { 
        status: "confirmed", 
        cancelRequestedByCustomer: false,
        rescheduleRequestedByCustomer: false
      });
    } else if (type === "payment") {
      const pm = actionForm.paymentMethod || "Cash";
      if (pm !== "Cash" && !actionForm.referenceNumber?.trim()) {
        setActionError(`Reference number is required for ${pm} payments.`);
        return;
      }
      const extraHours = Number(actionForm.exceedingHours) || 0;
      updateAppointmentData(apt._id, { 
        status: "completed",
        paymentMethod: pm,
        referenceNumber: actionForm.referenceNumber?.trim(),
        exceedingHours: extraHours
      });
    }
  };

  const toggleReadyForPickup = (apt) => {
    updateAppointmentData(apt._id, { isReadyForPickup: !apt.isReadyForPickup });
  };

  // Status filter
  let filtered = appointments.filter(a => 
    filter === "all" || 
    a.status === filter || 
    (filter === "cancelled" && (a.status === "cancelled" || a.status === "rejected")) ||
    (filter === "requests" && (a.status === "cancel_requested" || a.status === "reschedule_requested"))
  );

  if (petFilter) {
    filtered = filtered.filter(a =>
      (a.pets || []).some(
        p => p.petName?.toLowerCase() === petFilter.petName?.toLowerCase() &&
             p.petType === petFilter.petType
      ) && a.status !== "completed" && a.status !== "cancelled" && a.status !== "rejected"
    );
  }

  // --- Calendar Logic ---
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = Array(firstDay).fill(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    const monthName = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
    const today = new Date();

    return (
      <div className="bg-white p-4 sm:p-6 rounded-3xl border-2 border-purple-50 shadow-sm animate-fade-in">
        <div className="flex justify-between items-center mb-6">
          <button onClick={prevMonth} className="p-2 bg-purple-50 hover:bg-purple-100 rounded-xl text-purple-600 font-bold transition-colors text-sm">&larr; Prev</button>
          <h3 className="text-xl font-black text-gray-800">{monthName}</h3>
          <button onClick={nextMonth} className="p-2 bg-purple-50 hover:bg-purple-100 rounded-xl text-purple-600 font-bold transition-colors text-sm">Next &rarr;</button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div key={d} className="text-center text-xs font-black text-purple-400 uppercase py-2">{d}</div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((dateObj, i) => {
            if (!dateObj) return <div key={`empty-${i}`} className="min-h-[100px] bg-gray-50/50 rounded-2xl border border-gray-100 hidden sm:block"></div>;
            
            // Find appointments for this day
            const dayApts = filtered.filter(a => {
              // Find the earliest date among all pets for this appointment
              let aptDate = null;
              if (a.pets && a.pets.length > 0) {
                const dates = a.pets.map(p => {
                  if (p.serviceType === "hotel" && p.hotel?.checkInDate) return new Date(p.hotel.checkInDate);
                  if (p.grooming?.date) return new Date(p.grooming.date);
                  return null;
                }).filter(Boolean);
                if (dates.length > 0) {
                  aptDate = new Date(Math.min(...dates.map(d => d.getTime())));
                }
              }
              // Fallback to createdAt if no scheduled date exists
              if (!aptDate) aptDate = new Date(a.createdAt);
              
              return aptDate.getFullYear() === dateObj.getFullYear() && 
                     aptDate.getMonth() === dateObj.getMonth() && 
                     aptDate.getDate() === dateObj.getDate();
            });

            const isToday = today.toDateString() === dateObj.toDateString();
            const isClosed = dateObj.getDay() === 1;

            return (
              <div key={i} className={`min-h-[100px] sm:min-h-[120px] p-1.5 sm:p-2 rounded-2xl border ${isToday ? "border-purple-400 bg-purple-50/30" : isClosed ? "border-amber-100 bg-amber-50/20" : "border-gray-100 bg-white"} flex flex-col`}>
                <div className="flex justify-between items-center mb-1">
                  {isClosed ? (
                    <span className="text-[9px] font-extrabold text-amber-600 uppercase tracking-wider">Closed</span>
                  ) : <span />}
                  <span className={`text-xs font-bold ${isToday ? "text-purple-600" : "text-gray-400"}`}>
                    {dateObj.getDate()}
                  </span>
                </div>
                <div className="flex-1 flex flex-col gap-1 overflow-y-auto max-h-[80px] sm:max-h-[100px] no-scrollbar">
                  {dayApts.map(apt => {
                    let colorClass = "bg-gray-100 text-gray-600";
                    if (apt.status === "pending") colorClass = "bg-amber-100 text-amber-700";
                    else if (apt.status === "confirmed") colorClass = "bg-blue-100 text-blue-700";
                    else if (apt.status === "completed") colorClass = "bg-emerald-100 text-emerald-700";
                    else if (apt.status.includes("requested")) colorClass = "bg-orange-100 text-orange-700 animate-pulse";
                    
                    const isPast = dateObj < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    
                    if (isPast) return null;

                    return (
                      <div 
                        key={apt._id} 
                        onClick={() => setActionModal({ isOpen: true, apt, type: "cancel" })}
                        className={`text-[9px] sm:text-[10px] p-1.5 rounded-lg truncate cursor-pointer hover:opacity-80 font-bold ${colorClass}`}
                        title={`${apt.ownerName} - ${apt.status.replace("_", " ")}`}
                      >
                        {apt.ownerName.split(" ")[0]}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  // -------------------------

  if (loading) return <div className="text-center py-20 text-purple-600 font-bold">Loading appointments...</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl md:text-2xl font-black text-gray-800">Recent Bookings</h2>
          <button 
            onClick={() => {
              if (setActiveTab) {
                setActiveTab("booking");
              } else {
                setIsWalkInModalOpen(true);
              }
            }}
            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl shadow-md hover:scale-105 transition-all text-sm flex items-center gap-2"
          >
            Book Walk-In
          </button>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          {/* Download Report Button */}
          <button
            onClick={() => setIsPdfModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 font-bold rounded-xl text-xs hover:bg-purple-200 transition-all border border-purple-200"
          >
           PDF Report
          </button>
          
          {/* View Toggle */}
          <div className="flex bg-purple-50 rounded-xl p-1 border border-purple-100">
            <button 
              onClick={() => setViewMode("list")} 
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all ${viewMode === "list" ? "bg-white text-purple-700 shadow-sm" : "text-purple-400 hover:text-purple-600"}`}
            >List</button>
            <button 
              onClick={() => setViewMode("calendar")} 
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all ${viewMode === "calendar" ? "bg-white text-purple-700 shadow-sm" : "text-purple-400 hover:text-purple-600"}`}
            >Calendar</button>
          </div>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-white border-2 border-purple-50 rounded-xl outline-none text-sm font-bold text-gray-600"
          >
            <option value="all">All Status</option>
            <option value="requests">Action Required (Requests)</option>
            <option value="pending">Pending (Needs Approval)</option>
            <option value="confirmed">Confirmed</option>
            <option value="ongoing">On Going</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled / Rejected</option>
          </select>
        </div>
      </div>

      {petFilter && (
        <div className="flex items-center justify-between bg-purple-50 border-2 border-purple-200 rounded-2xl px-5 py-3">
          <p className="text-sm font-bold text-purple-700">
            🐾 Filtering bookings for <span className="text-purple-900">{petFilter.petName}</span> ({petFilter.petType})
          </p>
          <button
            onClick={onClearFilter}
            className="text-xs font-black text-purple-500 hover:text-purple-800 transition-colors ml-4 whitespace-nowrap"
          >
            ✕ Clear Filter
          </button>
        </div>
      )}

      {viewMode === "calendar" ? renderCalendar() : (
        <div className="hidden md:block overflow-x-auto animate-fade-in">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b-2 border-purple-50">
                <th className="py-4 px-4 text-xs font-black text-purple-400 uppercase">Owner</th>
                <th className="py-4 px-4 text-xs font-black text-purple-400 uppercase">Pets</th>
                <th className="py-4 px-4 text-xs font-black text-purple-400 uppercase">Service</th>
                <th className="py-4 px-4 text-xs font-black text-purple-400 uppercase">Status</th>
                <th className="py-4 px-4 text-xs font-black text-purple-400 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(apt => (
                <tr key={apt._id} className="border-b border-purple-50 hover:bg-purple-50/30 transition-all group">
                  {/* 1. OWNER */}
                  <td className="py-5 px-4 align-top">
                    <div className="font-bold text-gray-800 text-sm flex items-center gap-1.5">
                      {apt.ownerName}
                      {apt.isWalkIn && <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] rounded-full uppercase tracking-wider font-bold">Walk-In</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{apt.ownerPhone}</div>
                    <div className="text-[11px] text-gray-400 truncate max-w-[150px]">{apt.ownerEmail}</div>
                  </td>

                  {/* 2. PETS */}
                  <td className="py-5 px-4 align-top">
                    <div className="space-y-1.5">
                      {(apt.pets || []).map((p, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                          <span>{p.petType === "cat" ? "🐱" : "🐶"}</span>
                          <span className="text-purple-700 font-bold">{p.petName}</span>
                          <span className="text-gray-400 font-normal capitalize">({p.pet?.breed || "Pet"})</span>
                        </div>
                      ))}
                    </div>
                  </td>

                  {/* 3. SERVICE */}
                  <td className="py-5 px-4 align-top">
                    <div className="space-y-1.5 text-xs">
                      {(apt.pets || []).map((p, i) => (
                        <div key={i} className="text-gray-700">
                          <span className="font-bold text-gray-800 capitalize">{p.serviceType === "both" ? "Hotel & Grooming" : p.serviceType}</span>
                          {p.grooming?.service && <span className="text-purple-600 font-semibold"> · {p.grooming.service}</span>}
                          {p.hotel?.roomType && <span className="text-amber-600 font-semibold"> · {p.hotel.roomType}</span>}
                          <div className="text-[11px] text-gray-500">
                            {p.grooming?.date ? new Date(p.grooming.date).toLocaleDateString("en-PH", { month: "short", day: "numeric" }) : (p.hotel?.checkInDate ? new Date(p.hotel.checkInDate).toLocaleDateString("en-PH", { month: "short", day: "numeric" }) : "")}
                            {p.grooming?.time ? ` at ${p.grooming.time}` : (p.hotel?.checkInTime ? ` (${p.hotel.checkInTime})` : "")}
                          </div>
                        </div>
                      ))}
                      <div className="font-black text-xs text-gray-900 pt-0.5">₱{(apt.totalPrice || 0).toLocaleString()}</div>
                    </div>
                  </td>

                  {/* 4. STATUS */}
                  <td className="py-5 px-4 align-top space-y-1.5">
                    <div>
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider inline-block ${
                        apt.status === "pending" ? "bg-amber-100 text-amber-700 border border-amber-200" :
                        apt.status === "confirmed" ? "bg-blue-100 text-blue-700 border border-blue-200" :
                        apt.status === "ongoing" ? "bg-indigo-100 text-indigo-700 border border-indigo-200" :
                        apt.status === "completed" ? "bg-emerald-100 text-emerald-700 border border-emerald-200" :
                        apt.status === "rejected" ? "bg-red-100 text-red-700 border border-red-200" :
                        apt.status === "cancelled" ? "bg-red-100 text-red-700 border border-red-200" :
                        "bg-orange-100 text-orange-700 border border-orange-200 animate-pulse"
                      }`}>
                        {apt.status === "ongoing" ? "On Going" : apt.status.replace("_", " ")}
                      </span>
                    </div>

                    {apt.isReadyForPickup && (
                      <div><span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200">🛍️ Ready for Pickup</span></div>
                    )}
                    {apt.customerConfirmation === "yes" && <div className="text-[11px] font-bold text-emerald-600">✅ Customer Confirmed</div>}
                    {apt.customerConfirmation === "no" && <div className="text-[11px] font-bold text-red-600">❌ Customer Declined</div>}
                    {apt.customerConfirmation === "pending" && apt.status === "confirmed" && <div className="text-[11px] font-bold text-gray-400">⏳ Confirmation Pending</div>}
                    
                    {apt.status === "rejected" && (
                      <div className="text-[11px] bg-red-50 text-red-700 p-1.5 rounded-lg border border-red-100 max-w-[170px]">
                        <strong>Reason:</strong> {apt.rejectReason || apt.cancelReason || "No reason given"}
                      </div>
                    )}
                    {apt.status === "cancelled" && (apt.cancelReason || apt.rejectReason) && (
                      <div className="text-[11px] bg-red-50 text-red-700 p-1.5 rounded-lg border border-red-100 max-w-[170px]">
                        <strong>Reason:</strong> {apt.cancelReason || apt.rejectReason}
                      </div>
                    )}
                    {apt.status === "cancel_requested" && (
                      <div className="text-[11px] bg-orange-50 text-orange-700 p-1.5 rounded-lg border border-orange-100 max-w-[170px]">
                        <strong>Cancel Req:</strong> {apt.cancelReason}
                      </div>
                    )}
                    {apt.status === "reschedule_requested" && (
                      <div className="text-[11px] bg-orange-50 text-orange-800 p-2.5 rounded-xl border border-orange-200 max-w-[220px] space-y-1">
                        <div className="font-bold text-orange-900 flex items-center gap-1">
                          <span>🔄</span> Reschedule Req.
                        </div>
                        <div className="text-[10px] space-y-0.5">
                          <div><span className="text-gray-400">Current:</span> <span className="line-through text-gray-500">{getApptScheduleDetails(apt).dateStr}</span></div>
                          <div><span className="font-bold text-orange-700">New:</span> <span className="font-bold text-orange-950">{apt.rescheduleRequestedDate ? new Date(apt.rescheduleRequestedDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "TBD"}{apt.rescheduleRequestedTime ? ` · ${apt.rescheduleRequestedTime}` : ""}</span></div>
                        </div>
                        {apt.rescheduleReason && (
                          <div className="text-[10px] text-gray-600 truncate">
                            <strong>Reason:</strong> {apt.rescheduleReason}
                          </div>
                        )}
                      </div>
                    )}
                  </td>

                  {/* 5. ACTIONS */}
                  <td className="py-5 px-4 text-right align-middle">
                    <button 
                      onClick={() => setAppointmentDetailsModal(apt)} 
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl shadow-sm hover:shadow transition-all text-xs font-bold inline-flex items-center gap-1.5"
                    >
                     View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "list" && (
        <div className="md:hidden space-y-4 animate-fade-in">
          {filtered.map(apt => (
            <div key={apt._id} className="bg-white p-5 rounded-3xl border-2 border-purple-50 shadow-sm space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold text-gray-800">
                    {apt.ownerName}
                    {apt.isWalkIn && <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] rounded-full uppercase tracking-wider font-bold">Walk-In</span>}
                  </div>
                  <div className="text-xs text-gray-500">{apt.ownerPhone}</div>
                </div>
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                  apt.status === "pending" ? "bg-amber-100 text-amber-600" :
                  apt.status === "confirmed" ? "bg-blue-100 text-blue-600" :
                  apt.status === "ongoing" ? "bg-indigo-100 text-indigo-700" :
                  apt.status === "completed" ? "bg-emerald-100 text-emerald-600" :
                  "bg-red-100 text-red-600"
                }`}>
                  {apt.status === "ongoing" ? "On Going" : apt.status.replace("_", " ")}
                </span>
              </div>

              {/* Pets & Service summary */}
              <div className="bg-purple-50/40 p-3 rounded-2xl border border-purple-100 space-y-1.5 text-xs">
                {(apt.pets || []).map((p, i) => (
                  <div key={i} className="flex justify-between items-center text-gray-700">
                    <span className="font-bold text-purple-800">{p.petType === "cat" ? "🐱" : "🐶"} {p.petName}</span>
                    <span className="capitalize text-gray-500">{p.serviceType === "both" ? "Hotel & Grooming" : p.serviceType}</span>
                  </div>
                ))}
              </div>

              {apt.isReadyForPickup && (
                <div className="text-xs font-bold bg-purple-100 text-purple-700 p-1.5 px-3 rounded-xl inline-block">🛍️ Ready for Pickup</div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-gray-500">
                  Total: <strong className="text-gray-800 text-sm">₱{(apt.totalPrice || 0).toLocaleString()}</strong>
                </span>
                <button 
                  onClick={() => setAppointmentDetailsModal(apt)} 
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl text-xs font-bold shadow-sm"
                >
                 View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-20 text-gray-400 italic">No appointments found.</div>
      )}

      {/* Action Modal */}
      {actionModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl relative flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-800 mb-1 capitalize">
                {actionModal.type === "reject" ? "Reject Booking" : `${actionModal.type.replace("_", " ")} Appointment`}
              </h2>
              {actionModal.type === "reject" && (
                <p className="text-xs text-gray-500">
                  The customer will receive an email and in-dashboard notification explaining why the booking could not be accepted.
                </p>
              )}
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 px-6 sm:px-8 space-y-4">

            
            {(actionModal.type === "cancel" || actionModal.type === "reject" || actionModal.type === "reschedule") && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">
                    {actionModal.type === "reject" ? "Select Rejection Reason" : "Select Reason"} <span className="text-red-500">*</span>
                  </label>
                  <select 
                    className={`w-full px-4 py-3 border rounded-xl outline-none ${actionError && !actionForm.reason ? "border-red-400 bg-red-50" : "border-gray-200"}`}
                    value={actionForm.reason}
                    onChange={e => { setActionForm({ ...actionForm, reason: e.target.value }); setActionError(null); }}
                  >
                    <option value="">-- Choose reason --</option>
                    {actionModal.type === "reject" ? (
                      <>
                        <option value="Fully booked / No available slots">Fully booked / No available slots</option>
                        <option value="Incomplete vaccination / medical requirements">Incomplete vaccination / medical requirements</option>
                        <option value="Store closed / maintenance">Store closed / maintenance</option>
                        <option value="Pet requirements not met">Pet requirements not met</option>
                        <option value="Invalid payment or downpayment issue">Invalid payment or downpayment issue</option>
                        <option value="Schedule conflict">Schedule conflict</option>
                        <option value="Others">Others</option>
                      </>
                    ) : (
                      <>
                        <option value="Schedule conflict">Schedule conflict</option>
                        <option value="Change of mind">Change of mind</option>
                        <option value="Pet sick">Pet sick</option>
                        <option value="Store closed">Store closed</option>
                        <option value="Others">Others</option>
                      </>
                    )}
                  </select>
                </div>
                {actionForm.reason === "Others" && (
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Please specify <span className="text-red-500">*</span></label>
                    <input 
                      type="text"
                      className={`w-full px-4 py-3 border rounded-xl outline-none ${actionError && !actionForm.customReason.trim() ? "border-red-400 bg-red-50" : "border-gray-200"}`}
                      value={actionForm.customReason}
                      placeholder={actionModal.type === "reject" ? "Explain why the booking is rejected..." : "Type your reason here..."}
                      onChange={e => { setActionForm({ ...actionForm, customReason: e.target.value }); setActionError(null); }}
                    />
                  </div>
                )}
              </div>
            )}

            {actionModal.type === "payment" && (() => {
              const hasHotel = actionModal.apt.pets?.some(p => p.serviceType === "hotel" || p.serviceType === "both" || p.hotel?.roomType);
              const extraHours = Math.max(0, Number(actionForm.exceedingHours) || 0);
              const extraFee = extraHours * 50;
              const prevExtraFee = actionModal.apt.pets?.reduce((acc, p) => acc + (p.hotel?.exceedingFee || ((p.hotel?.exceedingHours || 0) * 50)), 0) || 0;
              const baseTotal = (actionModal.apt.totalPrice || 0) - prevExtraFee;
              const finalTotal = baseTotal + extraFee;
              const balanceToPay = Math.max(0, finalTotal - (actionModal.apt.downpaymentAmount || 0));

              return (
              <div className="space-y-4">
                {/* Exceeding Time / Hours Adjustment for Hotel (₱50 / hour) */}
                {hasHotel && (
                  <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-black text-purple-800 uppercase tracking-wider flex items-center gap-1.5">
                        <span>⏱️</span> Hotel Exceeding Time / Hours
                      </span>
                      <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full border border-purple-200">
                        ₱50 / hour
                      </span>
                    </div>
                    {(() => {
                      const hotelPet = actionModal.apt.pets?.find(p => p.hotel?.roomType);
                      const coDate = hotelPet?.hotel?.checkOutDate;
                      const coTime = hotelPet?.hotel?.checkOutTime || hotelPet?.hotel?.checkInTime;
                      return coDate ? (
                        <div className="text-[11px] bg-white p-2 rounded-xl border border-purple-100 text-purple-900 font-semibold flex items-center gap-1.5">
                          <span>📅</span>
                          <span>
                            Standard Check-out: <strong>{new Date(coDate).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })} at {coTime || "N/A"}</strong>
                          </span>
                        </div>
                      ) : null;
                    })()}
                    <p className="text-xs text-purple-600 font-medium">
                      If the pet exceeded standard checkout time, add exceeding hours before checking out:
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center bg-white border-2 border-purple-200 rounded-2xl p-1 shadow-sm flex-1">
                        <button
                          type="button"
                          onClick={() => {
                            const nextVal = Math.max(0, extraHours - 1);
                            setActionForm(prev => ({ ...prev, exceedingHours: nextVal }));
                          }}
                          className="w-10 h-10 rounded-xl bg-purple-50 hover:bg-purple-600 hover:text-white text-purple-700 font-black text-lg flex items-center justify-center transition-all"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="72"
                          value={actionForm.exceedingHours ?? 0}
                          onChange={(e) => {
                            const val = Math.max(0, parseInt(e.target.value) || 0);
                            setActionForm(prev => ({ ...prev, exceedingHours: val }));
                          }}
                          className="w-full text-center font-black text-base text-gray-800 outline-none bg-transparent"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setActionForm(prev => ({ ...prev, exceedingHours: extraHours + 1 }));
                          }}
                          className="w-10 h-10 rounded-xl bg-purple-50 hover:bg-purple-600 hover:text-white text-purple-700 font-black text-lg flex items-center justify-center transition-all"
                        >
                          +
                        </button>
                      </div>
                      <span className="text-xs font-bold text-gray-600 pr-2">Hour{extraHours !== 1 ? "s" : ""}</span>
                    </div>
                    {extraHours > 0 && (
                      <div className="flex justify-between items-center text-xs font-bold text-purple-700 bg-white p-2.5 rounded-xl border border-purple-200">
                        <span>Exceeding Stay Charge ({extraHours} hr × ₱50):</span>
                        <span className="font-black text-purple-800">+ ₱{extraFee.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1 bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Total Amount:</span>
                    <span className="font-bold text-emerald-600">₱{finalTotal.toLocaleString()}</span>
                  </div>
                  {actionModal.apt.downpaymentAmount > 0 && (
                    <>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Downpayment Paid:</span>
                        <span className="font-bold text-red-400">− ₱{actionModal.apt.downpaymentAmount?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-gray-200 pt-2 mt-1">
                        <span className="font-black text-gray-800">Balance to Pay:</span>
                        <span className="font-black text-xl text-purple-600">₱{balanceToPay.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </div>
                {actionModal.apt.paymentMethod && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 space-y-3">
                    <p className="text-xs font-black text-blue-700 uppercase tracking-wider">Customer Downpayment Submission</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Method:</span>
                      <span className="font-bold text-gray-800">{actionModal.apt.paymentMethod}</span>
                    </div>
                    {actionModal.apt.downpaymentAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Downpayment:</span>
                        <span className="font-bold text-purple-700">₱{actionModal.apt.downpaymentAmount?.toLocaleString()}</span>
                      </div>
                    )}
                    {actionModal.apt.paymentMethod !== "Cash" && actionModal.apt.referenceNumber && actionModal.apt.referenceNumber !== "CASH" && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Reference #:</span>
                        <span className="font-bold font-mono text-gray-800">{actionModal.apt.referenceNumber}</span>
                      </div>
                    )}
                    {actionModal.apt.paymentScreenshot && (
                      <div>
                        <p className="text-xs font-bold text-gray-500 mb-2">Payment Screenshot:</p>
                        <div 
                          onClick={() => setZoomImage(actionModal.apt.paymentScreenshot)}
                          className="cursor-zoom-in group"
                        >
                          <img
                            src={actionModal.apt.paymentScreenshot}
                            alt="Payment Screenshot"
                            className="w-full max-h-48 object-contain rounded-xl border-2 border-blue-200 bg-blue-50 hover:border-blue-400 transition-all"
                          />
                          <p className="text-[10px] text-blue-600 font-semibold text-center mt-1 group-hover:underline">Click to view full size</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Select Payment Method</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none"
                    value={actionForm.paymentMethod || "Cash"}
                    onChange={e => {
                      setActionForm({ ...actionForm, paymentMethod: e.target.value, referenceNumber: "" });
                      setActionError(null);
                    }}
                  >
                    <option value="Cash">Cash</option>
                    <option value="GCash">GCash</option>
                    <option value="UnionBank">UnionBank</option>
                    <option value="BDO">BDO</option>
                  </select>
                </div>
                {actionForm.paymentMethod === "GCash" && (
                  <div className="rounded-2xl border-2 overflow-hidden border-blue-200">
                    {/* QR Header */}
                    <div className="px-4 py-2.5 flex items-center gap-2 bg-blue-500">
                      <span className="text-white text-lg">💙</span>
                      <p className="text-xs font-black text-white uppercase tracking-widest">
                        GCash Store QR Code
                      </p>
                    </div>
                    {/* QR Code Image */}
                    <div className="flex flex-col items-center py-4 px-6 bg-blue-50">
                      <p className="text-xs text-gray-500 mb-3 font-medium text-center">
                        Ask the customer to scan the QR code below to pay the balance.
                      </p>
                      <div className="bg-white p-3 rounded-2xl shadow-md border border-gray-200">
                        <img
                          src="/gcash_qr.png"
                          alt="GCash QR Code"
                          className="w-48 h-48 object-contain"
                        />
                      </div>
                      <p className="text-xs font-bold mt-3 text-blue-700">
                        Balance: ₱{Math.max(0, (actionModal.apt.totalPrice || 0) - (actionModal.apt.downpaymentAmount || 0)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
                {actionForm.paymentMethod === "UnionBank" && (
                  <div className="rounded-2xl border-2 overflow-hidden border-orange-200">
                    <div className="px-4 py-2.5 flex items-center gap-2 bg-orange-500">
                      <span className="text-white text-lg">🏦</span>
                      <p className="text-xs font-black text-white uppercase tracking-widest">
                        UnionBank Account Details
                      </p>
                    </div>
                    <div className="py-4 px-6 bg-orange-50 space-y-2 text-xs">
                      <div className="bg-white p-3.5 rounded-xl border border-orange-200 shadow-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-gray-500">Bank:</span><strong className="text-gray-800">UnionBank of the Philippines</strong></div>
                        <div className="flex justify-between"><span className="text-gray-500">Account Name:</span><strong className="text-gray-800">Furpect Tails Pet Care Center</strong></div>
                        <div className="flex justify-between"><span className="text-gray-500">Account #:</span><strong className="font-mono text-orange-700 text-sm">1094 2345 6789</strong></div>
                      </div>
                      <p className="text-xs font-bold text-center text-orange-800 mt-2">
                        Balance to pay: ₱{Math.max(0, (actionModal.apt.totalPrice || 0) - (actionModal.apt.downpaymentAmount || 0)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
                {actionForm.paymentMethod === "BDO" && (
                  <div className="rounded-2xl border-2 overflow-hidden border-indigo-200">
                    <div className="px-4 py-2.5 flex items-center gap-2 bg-indigo-600">
                      <span className="text-white text-lg">🏦</span>
                      <p className="text-xs font-black text-white uppercase tracking-widest">
                        BDO Account Details
                      </p>
                    </div>
                    <div className="py-4 px-6 bg-indigo-50 space-y-2 text-xs">
                      <div className="bg-white p-3.5 rounded-xl border border-indigo-200 shadow-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-gray-500">Bank:</span><strong className="text-gray-800">BDO Unibank, Inc.</strong></div>
                        <div className="flex justify-between"><span className="text-gray-500">Account Name:</span><strong className="text-gray-800">Furpect Tails Pet Care Center</strong></div>
                        <div className="flex justify-between"><span className="text-gray-500">Account #:</span><strong className="font-mono text-indigo-800 text-sm">0012 3456 7890</strong></div>
                      </div>
                      <p className="text-xs font-bold text-center text-indigo-900 mt-2">
                        Balance to pay: ₱{Math.max(0, (actionModal.apt.totalPrice || 0) - (actionModal.apt.downpaymentAmount || 0)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
                {actionForm.paymentMethod !== "Cash" && (
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">
                      Reference Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full px-4 py-3 border rounded-xl outline-none ${actionError && (!actionForm.referenceNumber || !actionForm.referenceNumber.trim()) ? "border-red-400 bg-red-50" : "border-gray-200"}`}
                      value={actionForm.referenceNumber || ""}
                      onChange={e => {
                        setActionForm({ ...actionForm, referenceNumber: e.target.value });
                        setActionError(null);
                      }}
                      placeholder="Enter reference number"
                    />
                  </div>
                )}
              </div>
            ); })()}

            {actionModal.type === "approve_reschedule" && actionModal.apt && (
              <div className="space-y-4 my-2">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-2xl space-y-3">
                  <p className="text-xs font-black uppercase tracking-wider text-orange-800">
                    🔄 Reschedule Details
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="bg-white/80 p-3 rounded-xl border border-orange-100">
                      <span className="text-[10px] font-bold text-gray-400 uppercase block mb-0.5">Current Date & Time</span>
                      <p className="font-bold text-gray-700 line-through">
                        {getApptScheduleDetails(actionModal.apt).dateStr}
                        {getApptScheduleDetails(actionModal.apt).timeStr !== "Not specified" ? ` at ${getApptScheduleDetails(actionModal.apt).timeStr}` : ""}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-xl border-2 border-orange-300 shadow-sm">
                      <span className="text-[10px] font-black text-orange-700 uppercase block mb-0.5">Requesting Reschedule To</span>
                      <p className="font-extrabold text-orange-950">
                        📅 {actionModal.apt.rescheduleRequestedDate ? new Date(actionModal.apt.rescheduleRequestedDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Date TBD"}
                        {actionModal.apt.rescheduleRequestedTime ? ` at ${actionModal.apt.rescheduleRequestedTime}` : ""}
                      </p>
                    </div>
                  </div>
                  {actionModal.apt.rescheduleReason && (
                    <p className="text-xs text-orange-900 bg-white/60 p-2.5 rounded-xl border border-orange-100">
                      <strong>Reason:</strong> {actionModal.apt.rescheduleReason}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 text-center font-medium">
                  Approving will update the appointment to the requested date and mark it as confirmed.
                </p>
              </div>
            )}

            {(actionModal.type === "approve_cancel" || actionModal.type === "reject_request") && (
              <p className="text-gray-600 font-medium my-4">
                Are you sure you want to {actionModal.type.replace("_", " ")} this request?
              </p>
            )}

            {actionError && (
              <p className="mt-2 text-xs text-red-500 font-bold bg-red-50 border border-red-100 px-4 py-2 rounded-xl">⚠️ {actionError}</p>
            )}

            {/* End scrollable area */}
            <div className="pb-4" />
            </div>

            {/* Fixed Footer */}
            <div className="flex gap-3 px-6 sm:px-8 py-4 border-t border-gray-100 flex-shrink-0">
              <button 
                onClick={handleActionSubmit} 
                className={`flex-1 py-3 text-white font-bold rounded-xl text-sm transition-colors ${
                  actionModal.type === "reject" || actionModal.type.includes("cancel") || actionModal.type === "reject_request" ? "bg-red-500 hover:bg-red-600" : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                {actionModal.type === "reject" ? "Reject Booking" : actionModal.type === "cancel" ? "Cancel Booking" : "Confirm"}
              </button>
              <button 
                onClick={() => { setActionModal({ isOpen: false, apt: null, type: null }); setActionError(null); }} 
                className="flex-1 py-3 bg-gray-100 text-gray-600 font-bold rounded-xl text-sm hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isWalkInModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl relative">
            <button 
              onClick={() => setIsWalkInModalOpen(false)}
              className="absolute top-6 right-6 w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full font-bold flex items-center justify-center text-xl transition-colors z-50"
            >
              &times;
            </button>
            <div className="p-2 sm:p-6 mt-8">
              <BookingForm 
                user={user} 
                isAdminMode={true} 
                onSuccess={() => {
                  setIsWalkInModalOpen(false);
                  fetchAppointments();
                }} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Appointment Details Modal */}
      {appointmentDetailsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={(e) => { if(e.target === e.currentTarget) setAppointmentDetailsModal(null); }}>
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-800">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <span>📋</span> Appointment Details
              </h2>
              <button onClick={() => setAppointmentDetailsModal(null)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white font-bold flex items-center justify-center transition-colors">×</button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6">
              <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{appointmentDetailsModal.ownerName}</h3>
                  <p className="text-sm text-gray-500">{appointmentDetailsModal.ownerEmail} • {appointmentDetailsModal.ownerPhone}</p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-500 uppercase">Total Price</span>
                  <p className="text-2xl font-black text-gray-800">₱{(appointmentDetailsModal.totalPrice || 0).toLocaleString()}</p>
                  {appointmentDetailsModal.downpaymentAmount > 0 && (
                    <p className="text-xs text-purple-600 font-bold mt-1">Downpayment: ₱{(appointmentDetailsModal.downpaymentAmount || 0).toLocaleString()}</p>
                  )}
                </div>
              </div>

              {/* ── RESCHEDULE REQUEST BANNER ── */}
              {appointmentDetailsModal.status === "reschedule_requested" && (
                <div className="p-4 bg-orange-50 border-2 border-orange-300 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-black uppercase tracking-wider text-orange-800 flex items-center gap-1.5">
                      🔄 Reschedule Request Pending
                    </p>
                    <span className="text-[10px] font-bold bg-orange-200/60 text-orange-900 px-2.5 py-0.5 rounded-full">
                      Action Required
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="bg-white/80 p-3 rounded-xl border border-orange-200">
                      <span className="text-[10px] font-bold text-gray-400 uppercase block mb-0.5">Current Date & Time</span>
                      <p className="font-bold text-gray-600 line-through">
                        {getApptScheduleDetails(appointmentDetailsModal).dateStr}
                        {getApptScheduleDetails(appointmentDetailsModal).timeStr !== "Not specified" ? ` at ${getApptScheduleDetails(appointmentDetailsModal).timeStr}` : ""}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-xl border-2 border-orange-400 shadow-sm">
                      <span className="text-[10px] font-black text-orange-700 uppercase block mb-0.5">Requesting Reschedule To</span>
                      <p className="font-extrabold text-orange-950">
                        📅 {appointmentDetailsModal.rescheduleRequestedDate ? new Date(appointmentDetailsModal.rescheduleRequestedDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "TBD"}
                        {appointmentDetailsModal.rescheduleRequestedTime ? ` at ${appointmentDetailsModal.rescheduleRequestedTime}` : ""}
                      </p>
                    </div>
                  </div>
                  {appointmentDetailsModal.rescheduleReason && (
                    <p className="text-xs text-orange-900 bg-white/70 p-2.5 rounded-xl border border-orange-100">
                      <strong>Reason:</strong> {appointmentDetailsModal.rescheduleReason}
                    </p>
                  )}
                </div>
              )}

              {/* ── PAYMENT SUMMARY CARD ── */}
              {(() => {
                const apt = appointmentDetailsModal;
                const total = apt.totalPrice || 0;
                const paid = apt.downpaymentAmount || 0;
                const balance = Math.max(0, total - paid);
                const isFullyPaid = balance === 0 || apt.paymentType === "full" || apt.status === "completed";
                const methodBg = 
                  apt.paymentMethod === "GCash" ? "bg-blue-50 border-blue-200" : 
                  apt.paymentMethod === "UnionBank" ? "bg-orange-50 border-orange-200" : 
                  apt.paymentMethod === "BDO" ? "bg-indigo-50 border-indigo-200" : 
                  "bg-emerald-50 border-emerald-200";
                const methodText = 
                  apt.paymentMethod === "GCash" ? "text-blue-700" : 
                  apt.paymentMethod === "UnionBank" ? "text-orange-700" : 
                  apt.paymentMethod === "BDO" ? "text-indigo-700" : 
                  "text-emerald-700";

                return (
                  <div className={`rounded-2xl border-2 overflow-hidden ${isFullyPaid ? "border-emerald-300" : "border-amber-200"}`}>
                    {/* Header */}
                    <div className={`px-4 py-3 flex items-center justify-between ${isFullyPaid ? "bg-emerald-500" : "bg-amber-500"}`}>
                      <p className="text-xs font-black text-white uppercase tracking-widest flex items-center gap-1.5">
                        💳 Payment Summary
                      </p>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ${isFullyPaid ? "bg-white text-emerald-700" : "bg-white text-amber-700"}`}>
                        {isFullyPaid ? "✅ Fully Paid" : "⏳ Balance Remaining"}
                      </span>
                    </div>

                    {/* Price breakdown */}
                    <div className="bg-white px-4 py-3 space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Total Amount:</span>
                        <span className="font-black text-gray-900">₱{total.toLocaleString()}</span>
                      </div>
                      {paid > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">
                            {apt.paymentType === "full" ? "Amount Paid (Full):" : "Downpayment Paid:"}
                          </span>
                          <span className="font-bold text-emerald-600">− ₱{paid.toLocaleString()}</span>
                        </div>
                      )}
                      <div className={`flex justify-between items-center pt-2 border-t border-gray-100 ${isFullyPaid ? "text-emerald-700" : "text-amber-700"}`}>
                        <span className="font-black text-sm">{isFullyPaid ? "✅ Paid in Full" : "Balance to Pay:"}</span>
                        <span className="font-black text-xl">₱{isFullyPaid ? "0" : balance.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Payment method details */}
                    {apt.paymentMethod && (
                      <div className={`px-4 py-3 border-t-2 space-y-2 text-xs ${methodBg}`}>
                        <p className={`font-black uppercase tracking-wider text-[10px] ${methodText}`}>
                          {apt.paymentMethod === "GCash" ? "💙" : apt.paymentMethod === "UnionBank" ? "🏦" : apt.paymentMethod === "BDO" ? "🏦" : "💵"} {apt.paymentMethod} Payment Details
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-gray-600">
                          <div>Method: <strong className="text-gray-800">{apt.paymentMethod}</strong></div>
                          {paid > 0 && <div>Paid: <strong className="text-gray-800">₱{paid.toLocaleString()}</strong></div>}
                          {apt.referenceNumber && apt.referenceNumber !== "CASH" && (
                            <div className="col-span-2">Ref #: <strong className="font-mono text-gray-800">{apt.referenceNumber}</strong></div>
                          )}
                        </div>
                        {apt.paymentScreenshot && (
                          <div className="pt-1">
                            <p className="text-xs font-bold text-gray-500 mb-1">Payment Screenshot:</p>
                            <div
                              onClick={() => setZoomImage(apt.paymentScreenshot)}
                              className="cursor-zoom-in group"
                            >
                              <img
                                src={apt.paymentScreenshot}
                                alt="Payment Screenshot"
                                className="w-full max-h-48 object-contain rounded-xl border-2 border-white bg-white hover:shadow-md transition-all"
                              />
                              <p className="text-[10px] text-blue-600 font-semibold text-center mt-1 group-hover:underline">Click to view full size</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}


              {appointmentDetailsModal.notes && (
                <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Notes / Special Instructions</p>
                  <p className="text-sm text-amber-900 font-medium">{appointmentDetailsModal.notes}</p>
                </div>
              )}

              <div className="space-y-4">
                <h4 className="text-lg font-bold text-gray-800 border-b border-gray-100 pb-2">Pets in this booking:</h4>
                
                {appointmentDetailsModal.pets?.map((pet, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-2xl p-4 border border-gray-100 space-y-4">
                    <div className="flex items-center gap-4 border-b border-gray-200 pb-3">
                      <div className="w-12 h-12 bg-white border border-gray-200 rounded-xl flex items-center justify-center text-2xl overflow-hidden shrink-0">
                        {pet.petType === "cat" ? "🐱" : "🐶"}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{pet.petName}</h3>
                        <p className="text-xs text-gray-500 capitalize">{pet.pet?.breed || "Mixed Breed"} • {pet.serviceType === "both" ? "Hotel & Grooming" : pet.serviceType}</p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-3 border border-gray-200">
                      <p className="text-xs font-bold text-blue-600 uppercase mb-2">🩺 Pet Info & Health</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="text-gray-500">Gender: <span className="font-bold text-gray-800 capitalize">{pet.pet?.gender || "Unknown"}</span></div>
                        <div className="text-gray-500">Birthday: <span className="font-bold text-gray-800">{pet.pet?.birthday ? new Date(pet.pet.birthday).toLocaleDateString("en-PH") : "Unknown"}</span></div>
                        <div className="text-gray-500 col-span-2">Vaccinated: <span className="font-bold text-gray-800">{pet.health?.isVaccinated ? `Yes (${pet.health.lastVaccinationDate ? new Date(pet.health.lastVaccinationDate).toLocaleDateString("en-PH") : ""})` : "No / Unknown"}</span></div>
                        {pet.health?.medicalConditions && <div className="text-gray-500 col-span-2">Medical Conditions: <span className="font-bold text-gray-800">{pet.health.medicalConditions}</span></div>}
                        {pet.health?.medication && <div className="text-gray-500 col-span-2">Medications: <span className="font-bold text-gray-800">{pet.health.medication}</span></div>}
                        {pet.pet?.behaviorNotes && <div className="text-gray-500 col-span-2">Behavior Notes: <span className="font-bold text-gray-800">{pet.pet.behaviorNotes}</span></div>}
                      </div>

                      {/* Vaccination Photo */}
                      {pet.health?.vaccinationPhotoUrl ? (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs font-bold text-emerald-600 uppercase mb-2">💉 Vaccination Record Photo</p>
                          <div 
                            onClick={() => setZoomImage(pet.health.vaccinationPhotoUrl)}
                            className="cursor-zoom-in group"
                          >
                            <img
                              src={pet.health.vaccinationPhotoUrl}
                              alt="Vaccination Record"
                              className="w-full max-h-48 object-contain rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 transition-all"
                            />
                            <p className="text-[10px] text-emerald-600 font-semibold text-center mt-1 group-hover:underline">Click to view full size</p>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs font-bold text-gray-400 uppercase mb-1">💉 Vaccination Record Photo</p>
                          <div className="w-full h-16 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center">
                            <p className="text-xs text-gray-400 font-medium">No photo uploaded</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {pet.grooming && pet.grooming.service && (
                      <div className="bg-white rounded-xl p-3 border border-gray-200">
                        <p className="text-xs font-bold text-purple-600 uppercase mb-2">✂️ Grooming</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-gray-500">Package: <span className="font-bold text-gray-800">{pet.grooming.service}</span></div>
                          {pet.grooming.size && <div className="text-gray-500">Size: <span className="font-medium text-gray-800">{pet.grooming.size}</span></div>}
                          <div className="text-gray-500">Date: <span className="font-medium text-gray-800">{pet.grooming.date ? new Date(pet.grooming.date).toLocaleDateString("en-PH") : "N/A"}</span></div>
                          <div className="text-gray-500">Time: <span className="font-medium text-gray-800">{pet.grooming.time}</span></div>
                          <div className="col-span-2 flex justify-between mt-1 pt-1 border-t border-gray-100">
                            <span className="font-bold text-gray-600">Price:</span> 
                            <span className="font-black text-gray-800">₱{pet.grooming.price?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {pet.hotel && pet.hotel.roomType && (
                      <div className="bg-white rounded-xl p-3 border border-gray-200">
                        <p className="text-xs font-bold text-amber-600 uppercase mb-2">🏨 Hotel ({pet.hotel.stayDays || 1} day{(pet.hotel.stayDays || 1) > 1 ? "s" : ""})</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-gray-500">Room: <span className="font-bold text-gray-800">{pet.hotel.roomType}</span></div>
                          <div className="text-gray-500">Duration: <span className="font-bold text-blue-700">{pet.hotel.stayDays || 1} day(s)</span></div>
                          <div className="text-gray-500 col-span-2">Check-in: <span className="font-medium text-gray-800">{pet.hotel.checkInDate ? new Date(pet.hotel.checkInDate).toLocaleDateString("en-PH") : "N/A"} at {pet.hotel.checkInTime}</span></div>
                          <div className="text-gray-500 col-span-2">Check-out: <span className="font-medium text-gray-800">{pet.hotel.checkOutDate ? new Date(pet.hotel.checkOutDate).toLocaleDateString("en-PH") : "N/A"}{pet.hotel.checkOutTime ? ` at ${pet.hotel.checkOutTime}` : ""}</span></div>
                          {Boolean(pet.hotel.exceedingHours) && (
                            <div className="text-purple-700 col-span-2 font-medium">Exceeding Time: +{pet.hotel.exceedingHours} hr(s) (₱{(pet.hotel.exceedingFee || pet.hotel.exceedingHours * 50).toLocaleString()})</div>
                          )}
                          <div className="col-span-2 flex justify-between mt-1 pt-1 border-t border-gray-100">
                            <span className="font-bold text-gray-600">Total Hotel Price:</span> 
                            <span className="font-black text-gray-800">₱{pet.hotel.totalPrice?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Product Add-Ons */}
              {appointmentDetailsModal.addOns?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-lg font-bold text-gray-800 border-b border-gray-100 pb-2 flex items-center gap-2">
                    <span>🛍️</span> Purchased Products & Add-Ons:
                  </h4>
                  <div className="bg-purple-50/50 rounded-2xl p-4 border border-purple-100 space-y-2">
                    {appointmentDetailsModal.addOns.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm py-1.5 border-b border-purple-100/60 last:border-0">
                        <div>
                          <p className="font-bold text-gray-800">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.variant} • Qty: {item.qty}</p>
                        </div>
                        <span className="font-black text-purple-700">₱{(item.subtotal || (item.unitPrice * item.qty) || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Dynamic Actions in Details Modal */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center justify-end gap-2">
              <button 
                onClick={() => setAppointmentDetailsModal(null)} 
                className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl text-xs transition-colors"
              >
                Close
              </button>

              {/* IF PENDING (Not Approved) */}
              {appointmentDetailsModal.status === "pending" && (
                <>
                  <button 
                    onClick={() => {
                      const apt = appointmentDetailsModal;
                      setAppointmentDetailsModal(null);
                      setActionModal({ isOpen: true, apt, type: "reject" });
                    }} 
                    className="px-5 py-2.5 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white font-bold rounded-xl text-xs transition-colors border border-red-200 shadow-sm"
                  >
                    Reject Booking
                  </button>
                  <button 
                    onClick={() => {
                      updateAppointmentData(appointmentDetailsModal._id, { status: "confirmed" });
                      setAppointmentDetailsModal(null);
                    }} 
                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5"
                  >
                    <span>✅</span> Approve Booking
                  </button>
                </>
              )}

              {/* IF APPROVED (confirmed or ongoing) */}
              {(appointmentDetailsModal.status === "confirmed" || appointmentDetailsModal.status === "ongoing") && (
                <>
                  {/* On Going button */}
                  {appointmentDetailsModal.status !== "ongoing" ? (
                    <button 
                      onClick={() => {
                        updateAppointmentData(appointmentDetailsModal._id, { status: "ongoing" });
                        setAppointmentDetailsModal(prev => ({ ...prev, status: "ongoing" }));
                      }} 
                      className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow-sm transition-all flex items-center gap-1.5"
                    >
                      <span>✂️</span> Mark as On Going
                    </button>
                  ) : (
                    <button 
                      onClick={() => {
                        updateAppointmentData(appointmentDetailsModal._id, { status: "confirmed" });
                        setAppointmentDetailsModal(prev => ({ ...prev, status: "confirmed" }));
                      }} 
                      className="px-4 py-2.5 bg-blue-100 text-blue-800 font-bold rounded-xl text-xs border border-blue-300 transition-all flex items-center gap-1.5"
                      title="Click to revert to Confirmed"
                    >
                      <span>✂️</span> On Going (Active)
                    </button>
                  )}

                  {/* Ready for Pickup button */}
                  <button 
                    onClick={() => {
                      toggleReadyForPickup(appointmentDetailsModal);
                      setAppointmentDetailsModal(prev => ({ ...prev, isReadyForPickup: !prev.isReadyForPickup }));
                    }} 
                    className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 ${
                      appointmentDetailsModal.isReadyForPickup 
                        ? "bg-purple-100 text-purple-800 border-2 border-purple-300" 
                        : "bg-purple-600 hover:bg-purple-700 text-white"
                    }`}
                  >
                    <span>🛍️</span> {appointmentDetailsModal.isReadyForPickup ? "Ready for Pickup (Marked)" : "Ready for Pickup"}
                  </button>

                  {/* Proceed to Payment button */}
                  <button 
                    onClick={() => {
                      const apt = appointmentDetailsModal;
                      setAppointmentDetailsModal(null);
                      setActionModal({ isOpen: true, apt, type: "payment" });
                    }} 
                    className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs shadow-sm transition-colors"
                  >
                    💳 Proceed to Payment
                  </button>

                  {/* Cancel button */}
                  <button 
                    onClick={() => {
                      const apt = appointmentDetailsModal;
                      setAppointmentDetailsModal(null);
                      setActionModal({ isOpen: true, apt, type: "cancel" });
                    }} 
                    className="px-4 py-2.5 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white font-bold rounded-xl text-xs transition-colors border border-red-200"
                  >
                    Cancel Booking
                  </button>
                </>
              )}

              {/* Customer Requests */}
              {appointmentDetailsModal.status === "cancel_requested" && (
                <>
                  <button 
                    onClick={() => {
                      const apt = appointmentDetailsModal;
                      setAppointmentDetailsModal(null);
                      setActionModal({ isOpen: true, apt, type: "approve_cancel" });
                    }} 
                    className="px-4 py-2.5 bg-red-600 text-white font-bold rounded-xl text-xs"
                  >
                    Approve Cancel
                  </button>
                  <button 
                    onClick={() => {
                      const apt = appointmentDetailsModal;
                      setAppointmentDetailsModal(null);
                      setActionModal({ isOpen: true, apt, type: "reject_request" });
                    }} 
                    className="px-4 py-2.5 bg-gray-200 text-gray-700 font-bold rounded-xl text-xs"
                  >
                    Reject Request
                  </button>
                </>
              )}

              {appointmentDetailsModal.status === "reschedule_requested" && (
                <>
                  <button 
                    onClick={() => {
                      const apt = appointmentDetailsModal;
                      setAppointmentDetailsModal(null);
                      setActionModal({ isOpen: true, apt, type: "approve_reschedule" });
                    }} 
                    className="px-4 py-2.5 bg-orange-600 text-white font-bold rounded-xl text-xs"
                  >
                    Approve Reschedule
                  </button>
                  <button 
                    onClick={() => {
                      const apt = appointmentDetailsModal;
                      setAppointmentDetailsModal(null);
                      setActionModal({ isOpen: true, apt, type: "reject_request" });
                    }} 
                    className="px-4 py-2.5 bg-gray-200 text-gray-700 font-bold rounded-xl text-xs"
                  >
                    Reject Request
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Image Zoom Overlay */}
      {zoomImage && (
        <div 
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-200"
          onClick={() => setZoomImage(null)}
        >
          <div 
            className="relative max-w-4xl max-h-[85vh] flex flex-col items-center justify-center animate-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={() => setZoomImage(null)}
              className="absolute -top-12 right-0 bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold transition-all shadow-md"
            >
              ×
            </button>
            <img 
              src={zoomImage} 
              alt="Zoomed Record" 
              className="max-w-full max-h-[80vh] rounded-2xl object-contain shadow-2xl border-4 border-white/10"
            />
            <p className="text-white/60 text-xs mt-3 font-semibold tracking-wider">Click anywhere outside to close</p>
          </div>
        </div>
      )}

      {/* PDF Date Range Report Generator Modal — rendered via Portal at body level */}
      {isPdfModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200" onClick={() => setIsPdfModalOpen(false)}>
          <div
            className="bg-white rounded-3xl w-full max-w-xl shadow-2xl relative animate-in zoom-in-95 duration-200 border border-purple-100 flex flex-col max-h-[92vh]"
            onClick={e => e.stopPropagation()}
          >
            {/* Sticky Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center text-xl shadow-sm">
                  📄
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-black text-gray-800">Export PDF Report</h3>
                  <p className="text-xs text-gray-400 font-semibold">Select date range &amp; filter parameters</p>
                </div>
              </div>
              <button
                onClick={() => setIsPdfModalOpen(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 font-bold flex items-center justify-center transition-colors text-lg shrink-0"
              >
                &times;
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
              {/* Date Preset Selector */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Select Date Range</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "all", label: "All Time" },
                    { id: "today", label: "Today" },
                    { id: "yesterday", label: "Yesterday" },
                    { id: "week", label: "This Week" },
                    { id: "month", label: "This Month" },
                    { id: "last_month", label: "Last Month" },
                    { id: "custom", label: "Custom Range" },
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPdfPreset(p.id)}
                      className={`p-2.5 rounded-xl border text-xs font-bold transition-all ${
                        pdfPreset === p.id
                          ? "bg-purple-600 border-purple-600 text-white shadow-md scale-[1.02]"
                          : "bg-purple-50/50 border-purple-100 text-gray-700 hover:bg-purple-100"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Date Pickers */}
              {pdfPreset === "custom" && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-purple-50/50 rounded-2xl border border-purple-100 animate-in fade-in duration-200">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={pdfStartDate}
                      onChange={e => setPdfStartDate(e.target.value)}
                      className="w-full p-3 bg-white border border-purple-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={pdfEndDate}
                      onChange={e => setPdfEndDate(e.target.value)}
                      className="w-full p-3 bg-white border border-purple-200 rounded-xl text-xs font-semibold outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              )}

              {/* Status Filter */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Filter by Status</label>
                <select
                  value={pdfStatusFilter}
                  onChange={e => setPdfStatusFilter(e.target.value)}
                  className="w-full p-3.5 bg-white border-2 border-purple-100 rounded-2xl text-xs font-bold text-gray-800 outline-none focus:border-purple-400"
                >
                  <option value="all">All Statuses (Pending, Confirmed, Completed, Cancelled)</option>
                  <option value="completed">Completed Only (Paid &amp; Finished)</option>
                  <option value="confirmed">Confirmed Only</option>
                  <option value="pending">Pending Only</option>
                  <option value="cancelled">Cancelled Only</option>
                </select>
              </div>

              {/* Report Preview Metrics Box */}
              {(() => {
                const { filteredApts, label, totalRev } = getPdfFilteredData();
                return (
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-900 to-indigo-950 text-white space-y-2 shadow-inner">
                    <div className="flex items-center justify-between text-xs border-b border-white/10 pb-2">
                      <span className="text-purple-200 font-medium">Selected Range:</span>
                      <span className="font-bold text-yellow-300 text-right ml-2">{label}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-1 text-center">
                      <div>
                        <p className="text-[10px] text-purple-300 uppercase tracking-wider font-bold">Total Matching</p>
                        <p className="text-lg font-black">{filteredApts.length} <span className="text-xs font-normal text-purple-200">records</span></p>
                      </div>
                      <div className="border-l border-white/10">
                        <p className="text-[10px] text-purple-300 uppercase tracking-wider font-bold">Completed Revenue</p>
                        <p className="text-lg font-black text-emerald-400">₱{totalRev.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Sticky Footer Actions */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
              <button
                type="button"
                onClick={() => setIsPdfModalOpen(false)}
                className="px-5 py-2.5 text-gray-500 font-bold hover:bg-gray-100 text-xs rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGeneratePdf}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-xs rounded-xl shadow-lg hover:scale-105 transition-all flex items-center gap-2"
              >
               Download PDF Report
              </button>
            </div>
          </div>
        </div>
      , document.body)}

    </div>
  );
};

export default AdminAppointments;
